import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Clock, Plus, Settings, User, DollarSign, Trash2, Edit2, Check, X, ExternalLink, Upload, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import MarkCompleteAndRate from '../components/MarkCompleteAndRate';
import AppShell from '../components/AppShell';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Scheduler() {
  const { user, token, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('services');
  const [profile, setProfile] = useState(null);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [clientBookings, setClientBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showAvailabilityForm, setShowAvailabilityForm] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const [newService, setNewService] = useState({
    name: '',
    description: '',
    duration: 30,
    price: 0,
    category: ''
  });

  const [newSlot, setNewSlot] = useState({
    day_of_week: 0,
    start_time: '09:00',
    end_time: '17:00'
  });

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [profileRes, servicesRes, availRes, providerBookingsRes, clientBookingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/scheduler/profile`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/scheduler/services`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/scheduler/availability`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/scheduler/bookings/provider`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/scheduler/bookings/my`, { headers: getAuthHeader() })
      ]);
      setProfile(profileRes.data);
      setServices(servicesRes.data);
      setAvailability(availRes.data);
      setBookings(providerBookingsRes.data);
      setClientBookings(clientBookingsRes.data);
    } catch (error) {
      console.error('Failed to fetch scheduler data:', error);
      toast.error('Failed to load scheduler data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateService = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/scheduler/services`, newService, { headers: getAuthHeader() });
      toast.success('Service created! Pending admin approval.');
      setShowServiceForm(false);
      setNewService({ name: '', description: '', duration: 30, price: 0, category: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create service');
    }
  };

  const handleUpdateService = async (serviceId, updates) => {
    try {
      await axios.put(`${API_URL}/api/scheduler/services/${serviceId}`, updates, { headers: getAuthHeader() });
      toast.success('Service updated');
      setEditingService(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update service');
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await axios.delete(`${API_URL}/api/scheduler/services/${serviceId}`, { headers: getAuthHeader() });
      toast.success('Service deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete service');
    }
  };

  const handleAddAvailability = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/scheduler/availability`, newSlot, { headers: getAuthHeader() });
      toast.success('Availability added');
      setShowAvailabilityForm(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add availability');
    }
  };

  const handleDeleteAvailability = async (slotId) => {
    try {
      await axios.delete(`${API_URL}/api/scheduler/availability/${slotId}`, { headers: getAuthHeader() });
      toast.success('Slot removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove slot');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API_URL}/api/scheduler/bookings/${bookingId}`, { status }, { headers: getAuthHeader() });
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    }
  };

  const handleExportICS = async (bookingId) => {
    try {
      const response = await axios.get(`${API_URL}/api/scheduler/bookings/${bookingId}/ics`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `booking_${bookingId}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Calendar file downloaded');
    } catch (error) {
      toast.error('Failed to export calendar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AppShell active="scheduler" title="Scheduler">
      <div className="border-b border-white/5 bg-brand-surface/50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calendar className="text-brand-gold" size={22} />
              <h1 className="text-xl font-playfair font-semibold text-white">My Scheduler</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/scheduler/settings')}
                className="flex items-center gap-2 px-4 py-2 text-gray-300 hover:text-white bg-white/5 border border-white/10 rounded-lg text-sm"
                data-testid="settings-btn"
              >
                <Settings size={16} />
                Settings
              </button>
              <button
                onClick={() => window.open(`/book/${user?.id}`, '_blank')}
                className="flex items-center gap-2 px-4 py-2 bg-brand-gold text-black rounded-lg hover:brightness-110 text-sm font-semibold"
                data-testid="view-booking-page-btn"
              >
                <ExternalLink size={18} />
                View Booking Page
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex gap-1 bg-muted p-1 rounded-xl mb-8 w-fit">
          {[
            { id: 'services', label: 'Services', icon: DollarSign },
            { id: 'availability', label: 'Availability', icon: Clock },
            { id: 'bookings', label: 'My Appointments', icon: Calendar },
            { id: 'client-bookings', label: 'My Bookings', icon: User }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-card text-primary shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">My Services</h2>
              <button
                onClick={() => setShowServiceForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                data-testid="add-service-btn"
              >
                <Plus size={18} />
                Add Service
              </button>
            </div>

            {/* Service Form Modal */}
            {showServiceForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-card rounded-2xl p-6 w-full max-w-md shadow-xl border border-border">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Create New Service</h3>
                  <form onSubmit={handleCreateService} className="space-y-4" data-testid="service-form">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Service Name *</label>
                      <input
                        type="text"
                        value={newService.name}
                        onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="e.g., 1-on-1 Consultation"
                        required
                        data-testid="service-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Category *</label>
                      <input
                        type="text"
                        value={newService.category}
                        onChange={(e) => setNewService({ ...newService, category: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="e.g., Coaching, Strategy, Training"
                        required
                        data-testid="service-category-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Description *</label>
                      <textarea
                        value={newService.description}
                        onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                        rows={3}
                        placeholder="Describe what clients will get..."
                        required
                        data-testid="service-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Duration (min) *</label>
                        <select
                          value={newService.duration}
                          onChange={(e) => setNewService({ ...newService, duration: parseInt(e.target.value) })}
                          className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                          data-testid="service-duration-select"
                        >
                          <option value={15}>15 min</option>
                          <option value={30}>30 min</option>
                          <option value={45}>45 min</option>
                          <option value={60}>60 min</option>
                          <option value={90}>90 min</option>
                          <option value={120}>2 hours</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Price ($)</label>
                        <input
                          type="number"
                          value={newService.price}
                          onChange={(e) => setNewService({ ...newService, price: parseFloat(e.target.value) || 0 })}
                          className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                          min="0"
                          step="0.01"
                          data-testid="service-price-input"
                        />
                      </div>
                    </div>
                    <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-3 flex items-start gap-2">
                      <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={16} />
                      <p className="text-sm text-amber-400">Services require admin approval before they become visible to clients.</p>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowServiceForm(false)}
                        className="flex-1 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                        data-testid="submit-service-btn"
                      >
                        Create Service
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Services List */}
            {services.length === 0 ? (
              <div className="bg-card rounded-2xl border border-border p-12 text-center">
                <DollarSign className="mx-auto text-muted-foreground mb-4" size={48} />
                <h3 className="text-lg font-medium text-foreground mb-2">No Services Yet</h3>
                <p className="text-muted-foreground mb-4">Create your first service to start accepting bookings</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {services.map(service => (
                  <div
                    key={service.id}
                    className="bg-card rounded-xl border border-border p-5 hover:shadow-md transition-shadow"
                    data-testid="service-card"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">{service.name}</h3>
                          {service.is_approved ? (
                            <span className="px-2 py-0.5 bg-green-900/30 text-green-400 text-xs rounded-full">Approved</span>
                          ) : service.is_flagged ? (
                            <span className="px-2 py-0.5 bg-red-900/30 text-red-400 text-xs rounded-full">Flagged</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-amber-900/30 text-amber-400 text-xs rounded-full">Pending</span>
                          )}
                          {!service.is_active && (
                            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full">Inactive</span>
                          )}
                        </div>
                        <p className="text-sm text-primary mb-2">{service.category}</p>
                        <p className="text-muted-foreground text-sm mb-3">{service.description}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {service.duration} min
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign size={14} />
                            {service.price > 0 ? `$${service.price}` : 'Free'}
                          </span>
                        </div>
                        {service.is_flagged && service.flag_reason && (
                          <div className="mt-3 p-2 bg-red-900/30 rounded-lg text-sm text-red-400">
                            <strong>Flag reason:</strong> {service.flag_reason}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleUpdateService(service.id, { is_active: !service.is_active })}
                          className={`p-2 rounded-lg ${service.is_active ? 'text-green-400 bg-green-900/30' : 'text-slate-400 bg-muted'}`}
                          title={service.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {service.is_active ? <Check size={18} /> : <X size={18} />}
                        </button>
                        <button
                          onClick={() => handleDeleteService(service.id)}
                          className="p-2 text-red-400 bg-red-900/30 rounded-lg hover:bg-red-900/30"
                          data-testid="delete-service-btn"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">Weekly Availability</h2>
              <button
                onClick={() => setShowAvailabilityForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                data-testid="add-availability-btn"
              >
                <Plus size={18} />
                Add Time Slot
              </button>
            </div>

            {/* Availability Form Modal */}
            {showAvailabilityForm && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-card rounded-2xl p-6 w-full max-w-md shadow-xl">
                  <h3 className="text-lg font-semibold mb-4">Add Availability Slot</h3>
                  <form onSubmit={handleAddAvailability} className="space-y-4" data-testid="availability-form">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Day</label>
                      <select
                        value={newSlot.day_of_week}
                        onChange={(e) => setNewSlot({ ...newSlot, day_of_week: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                        data-testid="availability-day-select"
                      >
                        {days.map((day, idx) => (
                          <option key={idx} value={idx}>{day}</option>
                        ))}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">Start Time</label>
                        <input
                          type="time"
                          value={newSlot.start_time}
                          onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                          data-testid="availability-start-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">End Time</label>
                        <input
                          type="time"
                          value={newSlot.end_time}
                          onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                          data-testid="availability-end-input"
                        />
                      </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => setShowAvailabilityForm(false)}
                        className="flex-1 px-4 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                        data-testid="submit-availability-btn"
                      >
                        Add Slot
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Availability Grid */}
            <div className="bg-card rounded-2xl border border-border overflow-hidden">
              {days.map((day, dayIdx) => {
                const daySlots = availability.filter(s => s.day_of_week === dayIdx);
                return (
                  <div key={dayIdx} className="flex border-b border-slate-100 last:border-b-0">
                    <div className="w-32 px-4 py-4 bg-muted font-medium text-foreground">
                      {day}
                    </div>
                    <div className="flex-1 px-4 py-4 flex flex-wrap gap-2">
                      {daySlots.length === 0 ? (
                        <span className="text-slate-400 text-sm">Not available</span>
                      ) : (
                        daySlots.map(slot => (
                          <div
                            key={slot.id}
                            className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 text-indigo-700 rounded-lg text-sm"
                          >
                            <Clock size={14} />
                            {slot.start_time} - {slot.end_time}
                            <button
                              onClick={() => handleDeleteAvailability(slot.id)}
                              className="text-indigo-400 hover:text-red-500 ml-1"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Provider Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-foreground">Appointments (As Provider)</h2>
            
            {bookings.length === 0 ? (
              <div className="bg-card rounded-2xl border border-border p-12 text-center">
                <Calendar className="mx-auto text-muted-foreground mb-4" size={48} />
                <h3 className="text-lg font-medium text-foreground mb-2">No Appointments Yet</h3>
                <p className="text-muted-foreground">When clients book your services, they'll appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map(booking => (
                  <div
                    key={booking.id}
                    className="bg-card rounded-xl border border-border p-5"
                    data-testid="booking-card"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">{booking.service_name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            booking.status === 'confirmed' ? 'bg-green-900/30 text-green-400' :
                            booking.status === 'pending' ? 'bg-amber-900/30 text-amber-400' :
                            booking.status === 'cancelled' ? 'bg-red-900/30 text-red-400' :
                            'bg-muted text-foreground'
                          }`}>
                            {booking.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Client: {booking.client_name} ({booking.client_email})
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {booking.booking_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {booking.start_time} - {booking.end_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <DollarSign size={14} />
                            ${booking.service_price}
                          </span>
                        </div>
                        {booking.client_notes && (
                          <p className="mt-2 text-sm text-muted-foreground bg-muted rounded-lg p-2">
                            <strong>Notes:</strong> {booking.client_notes}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
                              data-testid="confirm-booking-btn"
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                              className="px-3 py-1.5 bg-red-900/30 text-red-400 rounded-lg text-sm hover:bg-red-900/50"
                            >
                              Decline
                            </button>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <>
                            <button
                              onClick={() => handleUpdateBookingStatus(booking.id, 'completed')}
                              className="px-3 py-1.5 bg-primary text-white rounded-lg text-sm hover:bg-primary/90"
                            >
                              Complete
                            </button>
                            <button
                              onClick={() => handleExportICS(booking.id)}
                              className="px-3 py-1.5 bg-muted text-foreground rounded-lg text-sm hover:bg-muted"
                              title="Add to Apple Calendar"
                            >
                              .ics
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Client Bookings Tab */}
        {activeTab === 'client-bookings' && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-foreground">My Bookings (As Client)</h2>
            
            {clientBookings.length === 0 ? (
              <div className="bg-card rounded-2xl border border-border p-12 text-center">
                <User className="mx-auto text-muted-foreground mb-4" size={48} />
                <h3 className="text-lg font-medium text-foreground mb-2">No Bookings Yet</h3>
                <p className="text-muted-foreground">Bookings you make with other members will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {clientBookings.map(booking => (
                  <div
                    key={booking.id}
                    className="bg-card rounded-xl border border-border p-5"
                    data-testid="client-booking-card"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">{booking.service_name}</h3>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            booking.status === 'confirmed' ? 'bg-green-900/30 text-green-400' :
                            booking.status === 'pending' ? 'bg-amber-900/30 text-amber-400' :
                            booking.status === 'cancelled' ? 'bg-red-900/30 text-red-400' :
                            'bg-muted text-foreground'
                          }`}>
                            {booking.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          Provider: {booking.provider_name}
                        </p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {booking.booking_date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={14} />
                            {booking.start_time} - {booking.end_time}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {booking.status === 'pending' && (
                          <button
                            onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                            className="px-3 py-1.5 bg-red-900/30 text-red-400 rounded-lg text-sm hover:bg-red-900/50"
                          >
                            Cancel
                          </button>
                        )}
                        {booking.status === 'confirmed' && (
                          <button
                            onClick={() => handleExportICS(booking.id)}
                            className="px-3 py-1.5 bg-muted text-foreground rounded-lg text-sm hover:bg-muted"
                            title="Add to Apple Calendar"
                          >
                            Add to Calendar
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Mark-complete + rating card. Self-hides when escrow
                        isn't held or the user is not the client. */}
                    <div className="mt-4">
                      <MarkCompleteAndRate booking={booking} onDone={fetchData} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
